export default class Model {
	constructor(
		locations,
		countries,
		continents,
		pluses,
		intro,
		categories,
		engines,
		cart,
		navigation,
		finderDefaultImage,
	) {
		this._locations = locations;
		this._countries = countries;
		this._continents = continents;
		this._intro = intro;
		this._categories = categories;
		this._engines = engines;
		this._panelItems = this.parseData(this.initPanelItems(locations));
		this._pluses = pluses;
		this._cartItems = [];
		this._cart = cart;
		this._navigation = navigation;
		this._finderDefaultImage = finderDefaultImage;

		// Structure data to search more efficiently
		this._searchDetailByCategoriesStructure = this.initSearchDetailByCategoryId(
			this.panelItems.flatMap((item) => item.children),
		);
	}

	get finderDefaultImage() {
		return this._finderDefaultImage;
	}

	set panelItems(items) {
		this._panelItems = this.initPanelItems(items);
	}

	get panelItems() {
		return this._panelItems;
	}

	get pluses() {
		return this._pluses;
	}

	get intro() {
		return this._intro;
	}

	get categories() {
		return this._categories;
	}

	get engines() {
		return this._engines;
	}

	get cartItems() {
		if (this._cartItems.length === 0) {
			this.generateCartItems();
		}

		return this._cartItems;
	}

	get cart() {
		return this._cart;
	}

	get searchDetailByCategoriesStructure() {
		return this._searchDetailByCategoriesStructure;
	}

	/**
	 * Generates a list of card items.
	 * This approach allows defining various types as cart items, promoting decoupling.
	 * Type example: { type: "bookmark", id: "", text: {en: "", de: ""}, relationType: "solution", relationId: "" }
	 * @returns {Array} List of card items.
	 */
	generateCartItems() {
		this._cartItems = [];

		const traverse = (items, parentIndex = "") => {
			items.forEach((item, index) => {
				let currentId = parentIndex + index.toString();

				this._cartItems.push({
					type: "bookmark",
					id: Number(currentId),
					text: item.title,
					relationType: item.type,
					relationId: item.id,
				});

				if (item.children && item.children.length > 0) {
					traverse(item.children, currentId);
				}
			});
		};

		traverse(this.panelItems);
	}

	initPanelItems(panelitems) {
		const categoryMap = this.categories.reduce((map, category) => {
			map[category.id] = category;
			return map;
		}, {});

		const assignCategories = (item) => {
			if (item.categories && item.categories.length > 0) {
				item.categories = item.categories.map(
					(categoryId) => categoryMap[categoryId],
				);
			}
			if (item.children && item.children.length > 0) {
				item.children.forEach(assignCategories);
			}
		};

		panelitems.forEach(assignCategories);
		return panelitems;
	}

	initSearchDetailByCategoryId(panelItems) {
		const categories = {};
		panelItems.forEach((item) => {
			item.children.forEach((child) => {
				child.categories.forEach((category) => {
					if (!(category.id in categories)) {
						categories[category.id] = [];
					}
					child["parentId"] = child.id;
					categories[category.id].push(child);
				});
			});
		});
		return categories;
	}

	get navigationItems() {
		return this._navigation.map((nav) => ({
			...nav,
			panelItems: nav.panelItems
				.map((id) => this.panelItems.find((panel) => panel.id === id))
				.filter(Boolean),
		}));
	}

	// #########################

	getConnections(detail) {
		return [
			{ type: "address", content: detail.address },
			{ type: "contact", content: detail.contact },
		];
	}

	get countries() {
		return this._countries;
	}

	get continents() {
		return this._continents;
	}

	get locations() {
		return this._locations;
	}

	getLocationsOfCountry(countryId) {
		let locations = [];
		this.locations.forEach((location) => {
			if (location.countryId == countryId) {
				location["categories"] = this.getCategories(location);
				location["connections"] = this.getConnections(location);
				locations.push(location);
			}
		});
		return locations;
	}

	getConnections(detail) {
		return [
			{ type: "address", content: detail.address },
			{ type: "contact", content: detail.contact },
		];
	}

	getCategories(detail) {
		return this.categories.filter((category) => {
			return detail.categoryIds.includes(category.id);
		});
	}

	sortData(dataObject) {
		return dataObject.sort((a, b) =>
			a.item.text.en > b.item.text.en ? 1 : -1,
		);
	}

	parseData() {
		// Build COUNTRY object with locations
		let countries = [];
		this.countries.forEach((country) => {
			let locations = this.getLocationsOfCountry(country.id);
			countries.push({ item: country, children: locations });
		});

		// Build CONTINENTS with COUNTRY object
		let continents = [];
		this.continents.forEach((continent) => {
			let filteredCountries = countries.filter((country) => {
				return country.item.continentId == continent.id;
			});
			continents.push({
				item: continent,
				children: filteredCountries,
			});
		});
		return this.sortData(continents);
	}
}
